import { useState } from 'react';
import { IconButton, ListItemIcon, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import { CheckRounded as CheckIcon, LanguageRounded as LanguageIcon } from '@mui/icons-material';
import { useI18n } from '@/contexts/I18nContext';
import type { LanguageCode } from '@/i18n/catalog';

export default function LanguageSelector() {
  const { language, languages, setLanguage, t } = useI18n();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleSelect = (nextLanguage: LanguageCode) => {
    setLanguage(nextLanguage);
    setAnchorEl(null);
  };

  return (
    <>
      <Tooltip title={t('language.openSelector')}>
        <IconButton
          aria-label={t('language.openSelector')}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            color: 'text.primary',
            bgcolor: 'rgba(244, 255, 249, 0.075)',
            border: '1px solid rgba(226, 255, 238, 0.16)',
            backdropFilter: 'blur(16px) saturate(145%)',
            WebkitBackdropFilter: 'blur(16px) saturate(145%)',
            '&:hover': {
              bgcolor: 'rgba(118, 244, 184, 0.14)',
              borderColor: 'rgba(118, 244, 184, 0.36)',
            },
          }}
        >
          <LanguageIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem disabled>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
            {t('language.title')}
          </Typography>
        </MenuItem>
        {languages.map((option) => {
          const selected = option.code === language;

          return (
            <MenuItem key={option.code} selected={selected} onClick={() => handleSelect(option.code)}>
              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 170 }}>
                <ListItemIcon sx={{ minWidth: 24 }}>
                  {selected ? <CheckIcon fontSize="small" color="primary" /> : null}
                </ListItemIcon>
                <Stack spacing={0}>
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {option.nativeLabel}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.label}
                  </Typography>
                </Stack>
              </Stack>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
